import React from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';
import { ActionButtonsContainer } from '../styled/actionToolbar';
import PaymentSchemeDialog from '../dialogues/PaymentSchemeDialog';
import FirstPaymentLinkDialog from '../dialogues/FirstPaymentLinkDialog';
import AddFeeDialog from '../dialogues/AddFeeDialog';
import DeregisterEnachDialog from '../dialogues/DeregisterEnachDialog';
import ViewPaymentImageDialog from '../dialogues/ViewPaymentImageDialog';
import { StudentPaymentSchemeWithMetrics } from '../../../types/paymentScheme';
import { TransformedStudentDetail } from '../../../types/userType';
import { superAdminUsers } from '../../../constants/superAdminUsers';
import { RootState } from '../../../redux/store';

import { SnackbarSeverityType } from '../../../enums';
import { PaperWrapper } from '../../common/styled';
import Icons from '../../../components/icons';

interface ActionToolbarProps {
  userDetails: TransformedStudentDetail | null;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
  onRefreshFeeDetails: () => Promise<void>;
  studentPaymentSchemes: StudentPaymentSchemeWithMetrics[];
}

const ActionToolbar: React.FC<ActionToolbarProps> = ({
  userDetails,
  onShowToast,
  onRefreshFeeDetails,
  studentPaymentSchemes
}) => {
  const { paymentUser } = useSelector((state: RootState) => state.paymentUser);
  const [isPaymentSchemeDialogOpen, setIsPaymentSchemeDialogOpen] =
    React.useState(false);
  const [isFirstPaymentLinkDialogOpen, setIsFirstPaymentLinkDialogOpen] =
    React.useState(false);
  const [isAddFeeDialogOpen, setIsAddFeeDialogOpen] = React.useState(false);
  const [isDeregisterEnachDialogOpen, setIsDeregisterEnachDialogOpen] =
    React.useState(false);
  const [isViewPaymentImageDialogOpen, setIsViewPaymentImageDialogOpen] =
    React.useState(false);

  const buttons = [
    {
      text: 'Payment Scheme',
      icon: <Icons.CardTickOutlined />,
      onClick: () => setIsPaymentSchemeDialogOpen(true)
    },
    {
      text: 'First Payment Link',
      icon: <Icons.CardSendOutlined />,
      onClick: () => setIsFirstPaymentLinkDialogOpen(true)
    },
    {
      text: 'Add Fee',
      icon: <Icons.CardAddOutlined />,
      onClick: () => setIsAddFeeDialogOpen(true)
    },
    {
      text: 'Payment Image',
      icon: <Icons.GalleryOutlined />,
      onClick: () => setIsViewPaymentImageDialogOpen(true)
    }
  ];

  if (paymentUser && superAdminUsers.includes(paymentUser?.email)) {
    buttons.push({
      text: 'De-register ENACH',
      icon: <Icons.TransactionMinusOutlined />,
      onClick: () => setIsDeregisterEnachDialogOpen(true)
    });
  }

  return (
    <PaperWrapper mb='16px'>
      <ActionButtonsContainer>
        {buttons.map((button, index) => (
          <Button
            key={index}
            variant='contained'
            startIcon={button.icon}
            onClick={button.onClick}
          >
            {button.text}
          </Button>
        ))}
      </ActionButtonsContainer>
      <PaymentSchemeDialog
        open={isPaymentSchemeDialogOpen}
        onClose={() => {
          setIsPaymentSchemeDialogOpen(false);
          onRefreshFeeDetails();
        }}
        userDetails={userDetails}
        onShowToast={onShowToast}
      />
      <FirstPaymentLinkDialog
        open={isFirstPaymentLinkDialogOpen}
        onClose={() => {
          setIsFirstPaymentLinkDialogOpen(false);
          onRefreshFeeDetails();
        }}
        userDetails={userDetails}
        onShowToast={onShowToast}
      />
      <AddFeeDialog
        open={isAddFeeDialogOpen}
        onClose={() => {
          setIsAddFeeDialogOpen(false);
          onRefreshFeeDetails();
        }}
        school={userDetails?.studentDetails?.school}
        userId={userDetails?.studentDetails?.id}
        userDetails={userDetails}
        onShowToast={onShowToast}
      />
      <DeregisterEnachDialog
        open={isDeregisterEnachDialogOpen}
        onClose={() => {
          setIsDeregisterEnachDialogOpen(false);
          onRefreshFeeDetails();
        }}
        userId={userDetails?.studentDetails?.id}
        onShowToast={onShowToast}
      />
      <ViewPaymentImageDialog
        open={isViewPaymentImageDialogOpen}
        onClose={() => {
          setIsViewPaymentImageDialogOpen(false);
          onRefreshFeeDetails();
        }}
        userDetails={userDetails}
        allPaymentSchemes={studentPaymentSchemes}
      />
    </PaperWrapper>
  );
};

export default ActionToolbar;
